import { Box, type Theme } from "@mui/material";
import type { FC, ReactNode } from "react";
import type { DayToggleCell, DayToggleSize } from "./DayToggleGrid";
import { chipColorSx, chipGeometrySx, chipTypeSx, type DayToggleToneColors } from "./dayToggleChipSx";

type Props = {
	cell: DayToggleCell;
	cellSize: number;
	stretch: boolean;
	shape: "square" | "circle";
	size: DayToggleSize;
	toneColors: (theme: Theme) => DayToggleToneColors;
	/** The corner marker, when the cell has one. */
	children?: ReactNode;
};

/**
 * One day of a read-only `DayToggleGrid` strip: the same shape and fills as the interactive chip, drawn at full ink
 * (this mode shows a pattern, it doesn't offer one), hidden from assistive tech because the strip carries one name.
 */
export const ReadOnlyDayChip: FC<Props> = ({ cell, cellSize, stretch, shape, size, toneColors, children }) => (
	<Box
		component="span"
		aria-hidden
		data-testid={cell.dataTestId}
		sx={(theme) => ({
			...chipGeometrySx(cellSize, stretch, shape),
			// Centres the glyph the way a <button> does on its own.
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			boxSizing: "border-box",
			// A display has no `look`: it always draws the muted chip.
			...chipColorSx(cell, toneColors(theme), "muted"),
			...chipTypeSx(size, "muted"),
		})}
	>
		{cell.label}
		{children}
	</Box>
);
